import { ReactElement } from 'react'
import { useSnackbar } from 'notistack'

import Typography from '@mui/material/Typography'
import { ConsoleDialog } from '@/components/ConsoleDialog'
import { MUI_STANDARD_TRANSITION_DURATION } from '@/constants/mui'
import { useUnmountingDelay } from '@/hooks/useUnmountingDelay'

import { ActionMenuItem } from '../ActionMenuItem/ActionMenuItem'
import { useActionMenuContext } from '../context/ActionMenuContext'
import { MenuItemLike, WithSnackbarMessages } from '../index.types'

type Props<Data extends Record<string, unknown>> = MenuItemLike & {
  onClick: (row: Data) => Promise<void> | void
  isClickable?: (row: Data) => boolean
  confirmationMessage?: ((row: Data) => string) | string
  icon?: ((row: Data) => ReactElement) | ReactElement
  label: ((row: Data) => string) | string
  unmountDelay?: number
  disabled?: boolean
  disableSnackbar?: {
    successDisabled?: boolean
    errorDisabled?: boolean
  }
} & WithSnackbarMessages<Data>

/** Component to be used as a base for creating new ActionItems. NOT for standalone use (eg. ActionToggleTranscoderStateItem) */
export const ActionMenuDialogItem = <Data extends Record<string, unknown>>({
  displaySeparately,
  errorMessage,
  successMessage,
  confirmationMessage,
  onClick,
  isClickable,
  overrideText,
  label,
  icon,
  unmountDelay = MUI_STANDARD_TRANSITION_DURATION,
  disabled,
  disableSnackbar
}: Props<Data>) => {
  const { enqueueSnackbar } = useSnackbar()
  const { isOpen: dialogOpen, isMounted, open, close } = useUnmountingDelay(false, unmountDelay)
  const { row, closeMenu } = useActionMenuContext<Data>()

  if (isClickable && isClickable(row) === false) return null

  const handleClick = async () => {
    try {
      await onClick(row)
      if (disableSnackbar?.successDisabled) return
      const message =
        typeof successMessage === 'function'
          ? successMessage(row)
          : successMessage || 'Action completed successfully'
      enqueueSnackbar(message, { variant: 'success' })
    } catch (e) {
      if (disableSnackbar?.errorDisabled) return
      const message =
        typeof errorMessage === 'function'
          ? errorMessage(row)
          : errorMessage || 'Failed to complete action'
      enqueueSnackbar(message, { variant: 'error' })
    }
  }

  const handleDialogClose = () => {
    close()
    closeMenu?.()
  }

  const message =
    typeof confirmationMessage === 'function' ? confirmationMessage(row) : confirmationMessage
  const itemLabel = typeof label === 'function' ? label(row) : label
  const itemIcon = typeof icon === 'function' ? icon(row) : icon

  return (
    <>
      <ActionMenuItem
        disabled={disabled}
        text={itemLabel}
        icon={itemIcon}
        onClick={open}
        displaySeparately={displaySeparately}
      />
      {isMounted && (
        <ConsoleDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          title={overrideText || 'Confirm'}
          SaveButtonProps={{
            text: 'Confirm',
            onClick: () => {
              handleClick()
              handleDialogClose()
            }
          }}
          CloseButtonProps={{
            text: 'Cancel'
          }}
        >
          <Typography>{message || 'Are you sure?'}</Typography>
        </ConsoleDialog>
      )}
    </>
  )
}
